import { DateTime } from 'luxon';
import {
  CartesianGrid,
  Cell,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { DIVERSION_THRESHOLD_PERCENT, EstimationVariancePoint } from '@/features/TimeStatsModel';
import {
  paddedPeriodDomain,
  spreadSameDayPoints,
  yAxisDomain,
  yAxisTicks,
} from '@/features/TimeStatsModel/estimationVariance';

import { EstimationScatterActiveShape } from './EstimationScatterActiveShape';
import { EstimationScatterShape } from './EstimationScatterShape';
import { EstimationScatterTooltip } from './EstimationScatterTooltip';
import { EstimationStatusLegend } from './EstimationStatusLegend';
import { buildStatusLegend } from './statusColors';
import { CATEGORY_COLORS, formatYAxisTick, openTaskInTracker, xAxisTicks } from './utils';

import s from './TimeEstimationScatterChart.module.scss';

type EstimationVarianceAxisChartProps = {
  points: EstimationVariancePoint[];
  periodStart: DateTime | null;
  periodEnd: DateTime | null;
};

export const EstimationVarianceAxisChart: React.FC<EstimationVarianceAxisChartProps> = ({
  points,
  periodStart,
  periodEnd,
}) => {
  const chartPoints = spreadSameDayPoints(points);
  const xDomain =
    periodStart && periodEnd
      ? paddedPeriodDomain(periodStart, periodEnd)
      : ([0, 1] as [number, number]);
  const yDomain = yAxisDomain(chartPoints.map((point) => point.deltaPercent));
  const yTicks = yAxisTicks(yDomain);
  const legend = buildStatusLegend(points);
  const colorByStatus = Object.fromEntries(legend.map((item) => [item.statusKey, item.color]));

  return (
    <div className={s.chart__plot} data-testid="estimation-chart-plot">
      <ResponsiveContainer width="100%" height={340} minWidth={480} minHeight={320}>
        <ComposedChart data={chartPoints} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            type="number"
            dataKey="displayTimestamp"
            domain={xDomain}
            ticks={xAxisTicks(xDomain)}
            tickFormatter={(value: number) => DateTime.fromMillis(value).toFormat('dd.MM')}
          />
          <YAxis
            type="number"
            dataKey="deltaPercent"
            domain={yDomain}
            ticks={yTicks}
            tickFormatter={formatYAxisTick}
          />
          <ReferenceLine y={0} stroke="var(--secondary-color)" style={{ pointerEvents: 'none' }} />
          <ReferenceLine
            y={DIVERSION_THRESHOLD_PERCENT}
            stroke="var(--danger-color)"
            strokeDasharray="4 4"
            style={{ pointerEvents: 'none' }}
          />
          <Scatter
            dataKey="deltaPercent"
            name="Отклонение"
            cursor="pointer"
            shape={EstimationScatterShape}
            activeShape={EstimationScatterActiveShape}
            onClick={openTaskInTracker}
          >
            {chartPoints.map((point) => (
              <Cell
                key={point.taskKey}
                fill={colorByStatus[point.statusKey]}
                stroke={CATEGORY_COLORS[point.category]}
              />
            ))}
          </Scatter>
          <Tooltip
            content={<EstimationScatterTooltip colorByStatus={colorByStatus} />}
            cursor={false}
            shared={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <EstimationStatusLegend items={legend} />
    </div>
  );
};
